// src/pages/InvoiceEmailSettings.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { apiPost, apiGet, apiUpload } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { AuthMe } from "../types/auth";
import { AppleComboBox } from "../components/AppleComboBox";
import { Button } from "../components/Button";
import { A, pillClass } from "../styles/appleClasses";

type Opt<T extends string = string> = { id: T; label: string };
type HeaderLayout = "centered" | "split";
type SendSequence =
  | "whatsapp,email"
  | "email,whatsapp"
  | "whatsapp"
  | "email";

type SendSequenceOption = {
  id: SendSequence;
  label: string;
  desc: string;
};

const LAYOUT_OPTIONS: Opt<HeaderLayout>[] = [
  { id: "centered", label: "Centered logo/title" },
  { id: "split", label: "Company left • Logo right" },
];

const SEND_SEQUENCE_OPTIONS: SendSequenceOption[] = [
  {
    id: "whatsapp,email",
    label: "WhatsApp → Email",
    desc: "WhatsApp opens first. Email is still available.",
  },
  {
    id: "email,whatsapp",
    label: "Email → WhatsApp",
    desc: "Use email as the primary send path. WhatsApp remains available as the secondary channel.",
  },
  {
    id: "whatsapp",
    label: "WhatsApp only",
    desc: "Use WhatsApp as the sending channel for invoice sendoff.",
  },
  {
    id: "email",
    label: "Email only",
    desc: "Use email only. No WhatsApp launch during invoice sendoff.",
  },
];

const DISPLAY_KEYS = {
  "Centered logo/title": "invoiceSending.layouts.centered",
  "Company left • Logo right": "invoiceSending.layouts.split",
  "WhatsApp → Email": "invoiceSending.sequences.whatsappEmail",
  "WhatsApp opens first. Email is still available.": "invoiceSending.sequences.whatsappEmailHelp",
  "Email → WhatsApp": "invoiceSending.sequences.emailWhatsapp",
  "Use email as the primary send path. WhatsApp remains available as the secondary channel.": "invoiceSending.sequences.emailWhatsappHelp",
  "WhatsApp only": "invoiceSending.sequences.whatsappOnly",
  "Use WhatsApp as the sending channel for invoice sendoff.": "invoiceSending.sequences.whatsappOnlyHelp",
  "Email only": "invoiceSending.sequences.emailOnly",
  "Use email only. No WhatsApp launch during invoice sendoff.": "invoiceSending.sequences.emailOnlyHelp",
} as const;
const ERROR_KEYS = {
  load: "invoiceSending.errors.load",
  autoSend: "invoiceSending.errors.autoSend",
  save: "invoiceSending.errors.save",
  fileType: "invoiceSending.errors.fileType",
  fileSize: "invoiceSending.errors.fileSize",
  missingUrl: "invoiceSending.errors.missingUrl",
  upload: "invoiceSending.errors.upload",
} as const;

type InvoiceEmailConfig = {
  auto_email_invoices_default: boolean;
  invoice_send_sequence: SendSequence;
  invoice_whatsapp_template: string | null;

  from_name: string | null;
  reply_to: string | null;

  logo_url: string | null;
  brand_company_name: string | null;
  brand_address: string | null;
  brand_phone: string | null;
  footer_note: string | null;

  header_layout: HeaderLayout;
  show_company_name?: boolean;

  is_pro_user: boolean;
  updated_at: string | null;
};

const ALLOWED_MIME = ["image/png", "image/jpeg"];
const MAX_FILE_BYTES = 300 * 1024; // 300KB

const DEFAULT_WHATSAPP_TEMPLATE =
  "Hi {customer_name}, here is your invoice {invoice_number}: {invoice_url}";

const toSettingString = (v: string | null | undefined) => (v ?? "").trim();

function normalizeSendSequence(v: any): SendSequence {
  const s = String(v || "").trim().toLowerCase();
  if (
    s === "whatsapp,email" ||
    s === "email,whatsapp" ||
    s === "whatsapp" ||
    s === "email"
  ) {
    return s;
  }
  return "whatsapp,email";
}

function normalize(res: any): InvoiceEmailConfig {
  const base: InvoiceEmailConfig = {
    auto_email_invoices_default: true,
    invoice_send_sequence: "whatsapp,email",
    invoice_whatsapp_template: DEFAULT_WHATSAPP_TEMPLATE,

    from_name: "",
    reply_to: "",
    logo_url: null,
    brand_company_name: "",
    brand_address: "",
    brand_phone: "",
    footer_note: "",

    is_pro_user: false,
    updated_at: null,

    header_layout: "centered",
    show_company_name: true,
  };

  if (!res || typeof res !== "object") return base;

  return {
    auto_email_invoices_default:
      res.auto_email_invoices_default ?? base.auto_email_invoices_default,

    invoice_send_sequence: normalizeSendSequence(
      res.invoice_send_sequence ??
        res.send_sequence ??
        base.invoice_send_sequence,
    ),

    invoice_whatsapp_template:
      res.invoice_whatsapp_template ??
      res.whatsapp_template ??
      base.invoice_whatsapp_template,

    from_name: res.from_name ?? base.from_name,
    reply_to: res.reply_to ?? base.reply_to,
    logo_url: res.logo_url ?? res.url ?? base.logo_url,
    brand_company_name: res.brand_company_name ?? base.brand_company_name,
    brand_address: res.brand_address ?? base.brand_address,
    brand_phone: res.brand_phone ?? base.brand_phone,
    footer_note: res.footer_note ?? base.footer_note,

    is_pro_user: !!res.is_pro_user,
    updated_at: res.updated_at || res.updatedAt || base.updated_at,

    header_layout:
      res.header_layout === "split" || res.header_layout === "centered"
        ? res.header_layout
        : base.header_layout,

    show_company_name:
      typeof res.show_company_name === "boolean"
        ? res.show_company_name
        : base.show_company_name,
  };
}

function buildWhatsAppPreview(
  template: string,
  companyName: string,
  fromName: string,
  fallbackCompany: string,
) {
  const safeTemplate = template || DEFAULT_WHATSAPP_TEMPLATE;
  return safeTemplate
    .replace(/\{customer_name\}/g, "Alex")
    .replace(/\{invoice_number\}/g, "INV-00012")
    .replace(/\{invoice_url\}/g, "https://your-public-invoice-link")
    .replace(/\{amount_due\}/g, "128.00")
    .replace(/\{company_name\}/g, companyName || fromName || fallbackCompany);
}

export default function InvoiceEmailSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id?: string }>();
  const q = company_id ? `?company_id=${company_id}` : "";
  const homeURL = companyRoute(company_id, "/");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggleSaving, setToggleSaving] = useState(false);

  const [isPro, setIsPro] = useState(false);

  const [autoEmailEnabled, setAutoEmailEnabled] = useState(true);
  const [sendSequence, setSendSequence] =
    useState<SendSequence>("whatsapp,email");
  const [whatsappTemplate, setWhatsappTemplate] = useState(
    DEFAULT_WHATSAPP_TEMPLATE,
  );

  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [brandCompanyName, setBrandCompanyName] = useState("");
  const [brandAddress, setBrandAddress] = useState("");
  const [brandPhone, setBrandPhone] = useState("");
  const [footerNote, setFooterNote] = useState("");
  const [, setUpdatedAt] = useState<string | null>(null);
  const [headerLayout, setHeaderLayout] = useState<HeaderLayout>("centered");
  const [showCompanyName, setShowCompanyName] = useState<boolean>(true);

  const [err, setErr] = useState<{ raw: any } | { local: keyof typeof ERROR_KEYS; maxKB?: number } | null>(null);
  const [okMsg, setOkMsg] = useState<"saved" | "uploaded" | "removed" | null>(null);

  const fileRef = useRef<HTMLInputElement | null>(null);

  const locked = !isPro || loading;

  const labelCls = `block mb-1 ${A.label}`;
  const sectionTitle = "text-[13px] font-semibold text-gray-700";
  const inputCls = `${A.input} disabled:bg-gray-100 disabled:text-gray-400`;
  const textareaCls = `${A.input} h-auto py-2 min-h-[72px] disabled:bg-gray-100 disabled:text-gray-400`;
  const iconBtn = `${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;
  const alertBase = `rounded-2xl border px-4 py-3 ${A.body}`;

  const previewText = buildWhatsAppPreview(
    whatsappTemplate,
    brandCompanyName,
    fromName,
    t("invoiceSending.previewCompany"),
  );

  const selectedSendSequenceOption =
    SEND_SEQUENCE_OPTIONS.find((opt) => opt.id === sendSequence) ||
    SEND_SEQUENCE_OPTIONS[0];

  function applyConfig(norm: InvoiceEmailConfig) {
    setAutoEmailEnabled(norm.auto_email_invoices_default);
    setSendSequence(norm.invoice_send_sequence);
    setWhatsappTemplate(
      norm.invoice_whatsapp_template || DEFAULT_WHATSAPP_TEMPLATE,
    );

    setHeaderLayout(norm.header_layout);
    setShowCompanyName(!!norm.show_company_name);
    setFromName(norm.from_name || "");
    setReplyTo(norm.reply_to || "");
    setLogoUrl(norm.logo_url || null);
    setBrandCompanyName(norm.brand_company_name || "");
    setBrandAddress(norm.brand_address || "");
    setBrandPhone(norm.brand_phone || "");
    setFooterNote(norm.footer_note || "");
    setUpdatedAt(norm.updated_at || null);
    setIsPro((prev) => prev || norm.is_pro_user);
  }

  useEffect(() => {
    let active = true;

    apiGet<AuthMe>("/auth/me")
      .then((me) => {
        if (!active) return;
        const tier = (me.account_tier || "").toLowerCase();
        const userIsPro =
          !!me.is_user_pro ||
          tier === "pro" ||
          tier === "paid" ||
          tier === "enterprise";
        setIsPro(userIsPro);
      })
      .catch(() => {
        if (!active) return;
        setIsPro(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const raw = await apiGet<any>(`/invoices/settings/email${q}`);
        if (!active) return;
        applyConfig(normalize(raw));
      } catch (e: any) {
        setErr(e?.message ? { raw: e.message } : { local: "load" });
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [q]);

  function buildSavePayload() {
    const payload: Record<string, any> = {
      auto_email_invoices_default: !!autoEmailEnabled,
      invoice_send_sequence: sendSequence,
      invoice_whatsapp_template:
        toSettingString(whatsappTemplate) || DEFAULT_WHATSAPP_TEMPLATE,
      header_layout: headerLayout,
      show_company_name: !!showCompanyName,
      from_name: toSettingString(fromName),
      reply_to: toSettingString(replyTo),
      brand_company_name: toSettingString(brandCompanyName),
      brand_address: toSettingString(brandAddress),
      brand_phone: toSettingString(brandPhone),
      footer_note: toSettingString(footerNote),
    };

    if (logoUrl === "") payload.logo_url = "";

    return payload;
  }

  async function handleToggleAutoEmail(nextVal: boolean) {
    if (!isPro) return;

    setErr(null);
    setOkMsg(null);
    setToggleSaving(true);

    const prev = autoEmailEnabled;
    setAutoEmailEnabled(nextVal);

    try {
      const raw = await apiPost(`/invoices/settings/email${q}`, {
        auto_email_invoices_default: !!nextVal,
      });
      applyConfig(normalize(raw));
    } catch (e: any) {
      setAutoEmailEnabled(prev);
      const raw = e?.response?.data?.detail || e?.message;
      setErr(raw ? { raw } : { local: "autoSend" });
    } finally {
      setToggleSaving(false);
    }
  }

  async function handleSaveAll() {
    if (!isPro) return;

    setErr(null);
    setOkMsg(null);
    setSaving(true);

    try {
      const raw = await apiPost(`/invoices/settings/email${q}`, buildSavePayload());
      applyConfig(normalize(raw));
      setOkMsg("saved");
    } catch (e: any) {
      const raw = e?.response?.data?.detail || e?.message;
      setErr(raw ? { raw } : { local: "save" });
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!isPro) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setErr(null);
    setOkMsg(null);

    if (!ALLOWED_MIME.includes(file.type)) {
      setErr({ local: "fileType" });
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      setErr({ local: "fileSize", maxKB: Math.floor(MAX_FILE_BYTES / 1024) });
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    let missingUrlError: Error | undefined;
    try {
      const form = new FormData();
      form.append("file", file);

      const data = await apiUpload<{ logo_url?: string; url?: string }>(
        `/invoices/settings/logo${q}`,
        form,
      );

      const nextUrl = data.logo_url ?? data.url;
      if (!nextUrl) {
        missingUrlError = new Error("No URL returned.");
        throw missingUrlError;
      }

      setLogoUrl(nextUrl);
      setOkMsg("uploaded");

      try {
        const fresh = normalize(await apiGet<any>(`/invoices/settings/email${q}`));
        setUpdatedAt(fresh.updated_at || null);
        setIsPro((prev) => prev || !!fresh.is_pro_user);
      } catch {
        // non-fatal
      }
    } catch (e: any) {
      setErr(missingUrlError && e === missingUrlError
        ? { local: "missingUrl" }
        : e?.message ? { raw: e.message } : { local: "upload" });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 sm:pt-6`}
    >
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-2xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h1 className={`flex-1 text-center ${A.title}`}>
          {t("settings.invoiceSending")}
        </h1>

        <button
          type="button"
          onClick={() => navigate(homeURL)}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <div className="w-full max-w-2xl px-2 pb-16">
        <div className={`${A.card} p-6 space-y-8`}>
          {/* SECTION 1: Auto-send toggle */}
          <section>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-appleblue"
                disabled={loading || toggleSaving || !isPro}
                checked={autoEmailEnabled}
                onChange={(e) => handleToggleAutoEmail(e.target.checked)}
              />

              <div className="flex-1">
                <div className={sectionTitle}>
                  {t("invoiceSending.autoSend")}
                </div>

                {!isPro && (
                  <div className="mt-2 inline-flex items-center gap-2">
                    <span className={pillClass("brand")}>PRO</span>
                    <span className={`${pillClass("neutral")} py-1`}>
                      {t("invoiceSending.upgradeAutoSend")}
                    </span>
                  </div>
                )}

              </div>
            </div>
          </section>

          {/* SECTION 2: Send sequence */}
          <section className="border-t border-gray-100 pt-6">
            <div className={`${sectionTitle} mb-1`}>{t("invoiceSending.sendOrder")}</div>
            <div className={`${A.label} mb-3`}>
              {t("invoiceSending.sendOrderHelp")}
            </div>

            <label className="block">
              <AppleComboBox<SendSequenceOption>
                options={SEND_SEQUENCE_OPTIONS}
                value={selectedSendSequenceOption}
                onChange={(opt) => setSendSequence(opt?.id || "whatsapp,email")}
                getOptionLabel={(opt) => opt ? t(DISPLAY_KEYS[opt.label as keyof typeof DISPLAY_KEYS]) : ""}
                placeholder={t("invoiceSending.chooseSendOrder")}
                disabled={locked}
                preserveQueryOnDisplayChange
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
              />
            </label>

            <div className="mt-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-[13px] text-gray-700">
                {t(DISPLAY_KEYS[selectedSendSequenceOption.desc as keyof typeof DISPLAY_KEYS])}
              </div>
            </div>
          </section>

          {/* SECTION 3: WhatsApp template */}
          <section className="border-t border-gray-100 pt-6">
            <div className={`${sectionTitle} mb-1`}>{t("invoiceSending.whatsappMessage")}</div>
            <textarea
              rows={3}
              className={`${textareaCls} min-h-[96px]`}
              disabled={locked}
              value={whatsappTemplate}
              onChange={(e) => setWhatsappTemplate(e.target.value)}
              placeholder={DEFAULT_WHATSAPP_TEMPLATE}
            />

            <div className="mt-2 text-xs text-gray-500">
              {t("invoiceSending.availablePlaceholders")} {"{customer_name}"},{" "}
              {"{invoice_number}"}, {"{invoice_url}"}, {"{amount_due}"},{" "}
              {"{company_name}"}
            </div>

            <div className="mt-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500 mb-1">{t("changeAvatar.preview")}</div>
              <div className="text-[13px] text-gray-700 break-words">
                {previewText}
              </div>
            </div>
          </section>

          {/* SECTION 4: Email sender info */}
          <section className="border-t border-gray-100 pt-6">
            <div className={`${sectionTitle} mb-3`}>{t("invoiceSending.senderInfo")}</div>

            <div className="space-y-4">
              <label className="block">
                <div className={labelCls}>{t("invoiceSending.fromName")}</div>
                <input
                  className={inputCls}
                  value={fromName}
                  disabled={locked}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder={t("invoiceSending.fromNamePlaceholder")}
                />
              </label>

              <label className="block">
                <div className={labelCls}>{t("invoiceSending.replyTo")}</div>
                <input
                  className={inputCls}
                  value={replyTo}
                  disabled={locked}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder="billing@yourcompany.com"
                />
              </label>
            </div>
          </section>

          {/* SECTION 5: Header layout */}
          <section className="border-t border-gray-100 pt-6">
            <div className={`${sectionTitle} mb-3`}>{t("invoiceSending.header")}</div>

            <div className="space-y-3">
              <label className="block">
                <div className={labelCls}>{t("invoiceSending.layout")}</div>
                <AppleComboBox<Opt<HeaderLayout>>
                  options={LAYOUT_OPTIONS}
                  value={
                    LAYOUT_OPTIONS.find((o) => o.id === headerLayout) || null
                  }
                  onChange={(o) => setHeaderLayout(o ? o.id : "centered")}
                  getOptionLabel={(o) => o ? t(DISPLAY_KEYS[o.label as keyof typeof DISPLAY_KEYS]) : ""}
                  placeholder={t("invoiceSending.chooseLayout")}
                  disabled={locked}
                  preserveQueryOnDisplayChange
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                />
              </label>

              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-appleblue"
                  disabled={locked}
                  checked={showCompanyName}
                  onChange={(e) => setShowCompanyName(e.target.checked)}
                />
                <span className={A.body}>{t("invoiceSending.showCompanyName")}</span>
              </label>

              <div className={A.label}>
                {t("invoiceSending.companyNameTip")}
              </div>
            </div>
          </section>

          {/* SECTION 6: Logo */}
          <section className="border-t border-gray-100 pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className={`${sectionTitle} mb-1`}>{t("invoiceSending.logo")}</div>
                <div className={A.label}>
                  {t("invoiceSending.logoHelp")}
                </div>
              </div>

              {!isPro && <span className={pillClass("brand")}>PRO</span>}
            </div>

            <div className="flex items-center gap-4 mt-3">
              <div className="w-20 h-20 rounded-2xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={t("invoiceSending.logoPreview")}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-[11px] text-gray-400 text-center px-2">
                    {t("invoiceSending.noLogo")}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={locked}
                  onClick={() => fileRef.current?.click()}
                  minWidth="auto"
                >
                  {t("invoiceSending.uploadNew")}
                </Button>

                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  disabled={locked}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg"
                />

                {logoUrl !== null && logoUrl !== "" && (
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => {
                      if (!isPro) return;
                      setLogoUrl("");
                      setOkMsg("removed");
                    }}
                    className="text-xs text-red-600 hover:text-red-700 text-left disabled:text-gray-400"
                  >
                    {t("invoiceSending.removeLogo")}
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* SECTION 7: Business block */}
          <section className="border-t border-gray-100 pt-6">
            <div className={`${sectionTitle} mb-3`}>
              {t("invoiceSending.businessInfo")}
            </div>

            <div className="space-y-4">
              <label className="block">
                <div className={labelCls}>{t("invoiceSending.companyName")}</div>
                <input
                  className={inputCls}
                  disabled={locked}
                  value={brandCompanyName}
                  onChange={(e) => setBrandCompanyName(e.target.value)}
                  placeholder={t("invoiceSending.companyPlaceholder")}
                />
              </label>

              <label className="block">
                <div className={labelCls}>{t("directory.common.address")}</div>
                <textarea
                  rows={2}
                  className={textareaCls}
                  disabled={locked}
                  value={brandAddress}
                  onChange={(e) => setBrandAddress(e.target.value)}
                  placeholder={t("invoiceSending.addressPlaceholder")}
                />
              </label>

              <label className="block">
                <div className={labelCls}>{t("invoiceSending.phone")}</div>
                <input
                  className={inputCls}
                  disabled={locked}
                  value={brandPhone}
                  onChange={(e) => setBrandPhone(e.target.value)}
                  placeholder="+852 1234 5678"
                />
              </label>
            </div>
          </section>

          {/* SECTION 8: Footer note */}
          <section className="border-t border-gray-100 pt-6">
            <div className={`${sectionTitle} mb-1`}>{t("invoiceSending.footer")}</div>
            <div className={`${A.label} mb-3`}>
              {t("invoiceSending.footerHelp")}
            </div>

            <textarea
              rows={3}
              className={`${textareaCls} min-h-[96px]`}
              disabled={locked}
              value={footerNote}
              onChange={(e) => setFooterNote(e.target.value)}
              placeholder={t("invoiceSending.footerPlaceholder")}
            />
          </section>

          {err && (
            <div className={`${alertBase} border-red-200 bg-red-50 text-red-700`}>
              {"raw" in err ? err.raw : t(ERROR_KEYS[err.local], { maxKB: err.maxKB })}
            </div>
          )}

          {okMsg && (
            <div
              className={`${alertBase} border-green-200 bg-green-50 text-green-700`}
            >
              {okMsg === "saved" ? t("companyProfile.saved") : okMsg === "uploaded" ? t("invoiceSending.logoUploaded") : t("invoiceSending.logoRemoved")}
            </div>
          )}

          {!isPro && (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
              <div className={A.label}>
                {t("invoiceSending.proFeature")}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              disabled={!isPro || loading || saving}
              onClick={handleSaveAll}
              minWidth="auto"
              className="px-5 py-2"
            >
              {saving ? t("directory.common.savingDots") : t("invoiceSending.saveChanges")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}